import { useAppDispatch } from '@app/hooks';
import { removeAsyncJob } from '@app/features/asyncJobs/asyncJobsSlice';
import { useNavigate } from 'react-router-dom';
import type { AsyncJob, AsyncJobStatus } from '@app/features/asyncJobs/asyncJobsTypes';
import Button from '@components/button/Button';
import { motion } from 'framer-motion';
import { BiCheckCircle, BiErrorCircle, BiLoaderAlt, BiFile } from 'react-icons/bi';
import { MdContentCopy } from 'react-icons/md';

interface Props {
	job: AsyncJob;
}

const cardVariants = {
	hidden: { opacity: 0, x: 50 },
	visible: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: 50 },
};

const getStatusStyles = (status: AsyncJobStatus): React.CSSProperties => {
	const baseStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		padding: '4px 8px',
		borderRadius: '4px',
		fontSize: 'var(--fs-9)',
		fontWeight: 500,
		color: 'var(--white)',
	};

	const statusColors: Record<AsyncJobStatus, string> = {
		pending: 'var(--dark-gray)',
		running: 'var(--primary-bright)',
		completed: 'var(--bintact-bright)',
		failed: 'var(--error)',
		cancelled: 'var(--dark-gray)',
	};

	return { ...baseStyle, background: statusColors[status] };
};

const StatusBadge = ({ status }: { status: AsyncJobStatus }) => {
	const icons: Record<AsyncJobStatus, React.ReactNode> = {
		pending: <BiLoaderAlt size={12} className="spin" />,
		running: <BiLoaderAlt size={12} className="spin" />,
		completed: <BiCheckCircle size={12} />,
		failed: <BiErrorCircle size={12} />,
		cancelled: <BiErrorCircle size={12} />,
	};

	const labels: Record<AsyncJobStatus, string> = {
		pending: 'Pending',
		running: 'Running',
		completed: 'Complete',
		failed: 'Failed',
		cancelled: 'Cancelled',
	};

	return (
		<div style={getStatusStyles(status)}>
			{icons[status]}
			{labels[status]}
		</div>
	);
};

const styles: Record<string, React.CSSProperties> = {
	item: {
		background: 'var(--white)',
		borderRadius: '8px',
		border: '1px solid var(--border)',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
		padding: '16px',
		width: '100%',
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: '12px',
		paddingBottom: '8px',
		borderBottom: '1px solid var(--light-gray)',
	},
	title: {
		fontSize: 'var(--fs-11)',
		fontWeight: 600,
		color: 'var(--dark)',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	closeButton: {
		background: 'none',
		border: 'none',
		fontSize: 'var(--fs-14)',
		cursor: 'pointer',
		padding: '4px',
		color: 'var(--dark-gray)',
		display: 'flex',
		alignItems: 'center',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: '8px',
	},
	progressBar: {
		height: '8px',
		background: 'var(--light-gray)',
		borderRadius: '4px',
		overflow: 'hidden',
	},
	progressFill: {
		height: '100%',
		background: 'var(--primary-bright)',
		transition: 'width 0.3s ease',
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '8px',
		marginTop: '12px',
		paddingTop: '8px',
		borderTop: '1px solid var(--light-gray)',
	},
	errorBox: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '8px',
		padding: '8px',
		background: 'rgba(156, 0, 4, 0.1)',
		borderRadius: '4px',
		border: '1px solid var(--error)',
	},
	errorText: {
		fontSize: 'var(--fs-9)',
		color: 'var(--error)',
		lineHeight: 1.4,
	},
};

const JobStatusItem = ({ job }: Props) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const handleClose = () => {
		dispatch(removeAsyncJob(job.id));
	};

	const handleViewProject = () => {
		const projectId = job.result?.new_project_id;
		if (projectId) {
			navigate(`/project/${projectId}`);
			handleClose();
		}
	};

	const hasNewProject = Boolean(
		job.kind === 'project_copy' && job.result?.new_project_id
	);

	return (
		<motion.div
			style={styles.item}
			variants={cardVariants}
			initial="hidden"
			animate="visible"
			exit="exit"
			transition={{ duration: 0.2 }}
		>
			<div style={styles.header}>
				<div style={styles.title}>
					{job.kind === 'project_copy' 
						? <MdContentCopy size={14} /> 
						: <BiFile size={14} />
					}
					{job.kind === 'project_copy' ? 'Copying project...' : 'Generating report...'}
				</div>
				<button onClick={handleClose} style={styles.closeButton}>×</button>
			</div>

			<div style={styles.content}>
				<StatusBadge status={job.status} />

				{(job.status === 'pending' || job.status === 'running') && (
					<div style={styles.progressBar}>
						<div 
							style={{ 
								...styles.progressFill, 
								width: `${job.progress}%` 
							}}
						/>
					</div>
				)}

				{job.status === 'completed' && hasNewProject && (
					<div style={styles.actions}>
						<Button 
							onClick={handleViewProject}
							className="fs-9"
							style={{ 
								padding: '6px 12px',
								background: 'transparent',
								color: 'var(--primary-bright)',
								border: '1px solid var(--primary-bright)',
								borderRadius: '4px',
							}}
						>
							View project
						</Button>
					</div>
				)}

				{job.status === 'failed' && (
					<div style={styles.errorBox}>
						<BiErrorCircle size={14} style={{ color: 'var(--error)', flexShrink: 0 }} />
						<span style={styles.errorText}>
							{job.error_message || 'An error occurred'}
						</span>
					</div>
				)}
			</div>
		</motion.div>
	);
};

export default JobStatusItem;
